/**
 * The wall clock, as an external store.
 *
 * A countdown has to re-render once a second, and the current time is exactly the kind of
 * value React components may not read during render: it changes on its own, so reading it
 * mid-render makes a component's output depend on when it happened to render. Modelling the
 * clock as a subscribable store keeps `useCountdown` pure — it reads a snapshot React
 * controls, and the store is what talks to `Date.now()`.
 *
 * One interval serves every countdown on the page, and it only runs while something is
 * subscribed: a page with no countdown mounted keeps no timer alive.
 */

const TICK_MS = 1000;

let nowMs = Date.now();
let timer: ReturnType<typeof setInterval> | null = null;
const listeners = new Set<() => void>();

function tick(): void {
  nowMs = Date.now();
  for (const listener of listeners) listener();
}

export function subscribeToClock(listener: () => void): () => void {
  listeners.add(listener);
  if (timer === null) {
    // Refreshed on the first subscription so a countdown mounted long after page load does
    // not start from a snapshot taken at import time.
    nowMs = Date.now();
    timer = setInterval(tick, TICK_MS);
  }

  return () => {
    listeners.delete(listener);
    if (listeners.size === 0 && timer !== null) {
      clearInterval(timer);
      timer = null;
    }
  };
}

/** The cached instant. Stable between ticks, which is what makes it safe to read in render. */
export function clockSnapshot(): number {
  return nowMs;
}

/**
 * The snapshot used while the page is being prerendered (`WEB-044`, canon `D-020`).
 *
 * `useSyncExternalStore` requires one for server-rendered content, and the value has to be
 * stable across calls or React re-renders forever. It is the instant the build imported this
 * module — the only "now" that exists at build time.
 *
 * **What this means for anything that reads the clock.** The prerendered HTML is written
 * against build time and the browser corrects it on hydration. For a countdown that is
 * invisible: the section it belongs to is fetched live and has no data to count down to until
 * it arrives. For `D-019`'s stale-board guard it is a real, bounded limitation, and
 * `src/sections/BoardDateline.tsx` states it where a reader of that guard will find it.
 */
const BUILD_TIME_MS = nowMs;

export function clockServerSnapshot(): number {
  return BUILD_TIME_MS;
}
